import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Bars3Icon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import logo from '../../assets/images/vadom.jpeg';
import DownloadAppButton from '../DownloadAppButton';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout, isSmart } = useAuth();
  const location = useLocation(); // savoir sur quelle page on est

  const categories = [
    { name: 'Artisanat', slug: 'artisanat' },
    { name: 'Domicile', slug: 'domicile' },
    { name: 'Numérique', slug: 'numerique' },
    { name: 'Transport', slug: 'transport' },
    { name: 'Beauté', slug: 'beaute' },
    { name: 'Formation', slug: 'formation' },
  ];

  // Navigation principale
  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'Devenir Smart', path: '/become-smart' },
    { name: 'À propos', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 bg-[#111111]/95 backdrop-blur-sm border-b border-[#252525] z-50">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            
            <img 
              src={logo}
              alt="VADOM"
              className="h-8 w-auto rounded-xl"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span className="text-2xl font-display font-bold text-[#F97316]">
              VADOM
            </span>
            <span className="hidden sm:inline text-sm text-[#AAAAAA] font-medium">
              Services à domicile
            </span>
          </Link>

          {/* Navigation desktop */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#F97316] border-b-2 border-[#F97316] pb-1'
                    : 'text-[#AAAAAA] hover:text-[#F97316]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Actions desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <DownloadAppButton className="btn-white text-sm py-2 px-4 flex items-center space-x-2 cursor-pointer" />

            {user ? (
              <div className="flex items-center space-x-4">
                <Link
                  to={isSmart ? '/smart/dashboard' : '/dashboard'}
                  className="flex items-center space-x-2 text-[#AAAAAA] hover:text-[#F97316]"
                >
                  <UserCircleIcon className="h-6 w-6" />
                  <span>{user.nom}</span>
                </Link>
                <button onClick={logout} className="text-sm text-[#AAAAAA] hover:text-red-400">
                  Déconnexion
                </button>
              </div>
            ) : (
              <>
                <Link to="/login" className="text-[#AAAAAA] hover:text-[#F97316] font-medium">
                  Connexion
                </Link>
                <Link to="/become-smart" className="btn-primary text-sm py-2 px-4">
                  Devenir Smart
                </Link>
              </>
            )}
          </div>

          {/* Actions mobile : le téléchargement reste visible en permanence,
              il ne doit pas dépendre de l'ouverture du menu */}
          <div className="flex items-center gap-1 lg:hidden">
            <DownloadAppButton
              className="flex items-center gap-1.5 bg-[#F97316] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#e8630a] transition whitespace-nowrap"
              label="Télécharger"
              showSize={false}
            />

            <button
              className="p-2 text-[#AAAAAA]"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Catégories (desktop) */}
        <div className="hidden lg:flex items-center space-x-1 pb-3 overflow-x-auto">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/services/${cat.slug}`}
              className={`px-4 py-2 text-sm font-medium transition-all whitespace-nowrap rounded-full
                ${location.pathname === `/services/${cat.slug}` 
                  ? 'bg-[#F97316] text-white' 
                  : 'text-[#AAAAAA] hover:text-[#F97316] hover:bg-[#252525]'
                }`}
            >
              {cat.name}
            </Link>
          ))}
          <Link
            to="/services"
            className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all
              ${location.pathname === '/services' 
                ? 'bg-[#F97316] text-white' 
                : 'text-[#F97316] bg-[#252525] hover:bg-[#333333]'
              }`}
          >
            Voir tout →
          </Link>
        </div>
      </div>

      {/* Menu mobile */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-[#252525] bg-[#111111]">
          <div className="container-custom py-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-2 font-medium ${
                  location.pathname === link.path
                    ? 'text-[#F97316]'
                    : 'text-[#AAAAAA]'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {user ? (
              <div className="flex items-center space-x-4">
                <Link
                  to={isSmart ? '/smart/dashboard' : '/dashboard'}
                  className="flex items-center space-x-2 text-[#AAAAAA] hover:text-[#F97316]"
                >
                  <UserCircleIcon className="h-6 w-6" />
                  <span>{user.nom}</span>
                </Link>
                <button onClick={logout} className="text-sm text-[#AAAAAA] hover:text-red-400">
                  Déconnexion
                </button>
              </div>
            ) : (
              <>
                {/* Le téléchargement a sa place dans la barre du haut, pas ici */}
                <Link to="/login" className="btn-primary block text-center" onClick={() => setIsMenuOpen(false)}>
                  Connexion
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;